import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgSelectOption,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { map, startWith, Subject, takeUntil } from 'rxjs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatPseudoCheckboxModule } from '@angular/material/core';
import {
  NgOptgroupTemplateDirective,
  NgOptionTemplateDirective,
  NgSelectComponent,
  NgSelectModule,
} from '@ng-select/ng-select';
import { MatIcon } from '@angular/material/icon';
import { formatDisplayText, formatValueSafely } from '../../utils/text-case.utils';

/** One entry of the buttons/tiles surface: either a plain option pill or a
 *  group pill that opens an inline submenu of its child options. A single
 *  interface (kind discriminates) keeps the template's type checking happy. */
interface TopLevelEntry {
  kind: 'option' | 'group';
  value?: any;
  label?: string;
  /** Group only: display-only pill text — matching/anchoring stays on `label`. */
  displayLabel?: string;
  children?: any[];
  /** Group only: render the "All <group>" entry in the tray (default true). */
  showAll?: boolean;
}

@Component({
  selector: 'app-global-dropdown',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatPseudoCheckboxModule,
    NgSelectComponent,
    FormsModule,
    NgOptionTemplateDirective,
    NgSelectModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GlobalDropdownComponent),
      multi: true,
    },
  ],
  templateUrl: './global-dropdown.component.html',
  styleUrl: './global-dropdown.component.scss',
})
export class GlobalDropdownComponent implements ControlValueAccessor, OnChanges {
  @Input() data: any;
  @Input() options: any[] = [];
  @Output() filterChanged = new EventEmitter<any>();
  private suppressSelectionChange = false;
  private destroy$ = new Subject<void>();

  selected: any[] = [];
  searchCtrl = new FormControl('');
  filteredOptions: any[] = [];
  isDropdownOpen = false;

  selectedDropdownList: { [key: string]: any[] } = {};

  private onChangeFn: (value: any) => void = () => {};
  private onTouchedFn: () => void = () => {};

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private globalFilterService: GlobalFilterService,
  ) {}

  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchText: string | null) => {
        const term = (searchText || '').toLowerCase();
        if (!term) {
          this.filteredOptions = [...this.options];
        } else {
          this.filteredOptions = this.options.filter((opt) => opt.toLowerCase().includes(term));
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!this.data?.apiUrl) {
      this.configService.getConfig(this.data?.apiUrl, this.data,this.data.method).subscribe({
        next: (res) => {
          this.options = this.applyConfiguredOrder(this.uniqueOptions(res?.data || []));
          this.filteredOptions = this.options;
          this.data.optionsList = this.options;
        },
        error: (err) => console.error(err),
      });
    } else {
      // Apply the configured option order HERE rather than trusting the order
      // the options arrived in. The builder re-orders as it rebuilds a tile, so
      // it looked right there, while the published view renders the `optionsList`
      // baked at publish time — which is stale unless the author pressed
      // Generate after setting the order. Ordering in the component makes every
      // host agree, and is idempotent over an already-ordered list.
      this.options = this.applyConfiguredOrder(this.uniqueOptions(this.options || []));
      this.filteredOptions = this.options || [];
    }
  }

  get isAllSelected(): boolean {
    return this.options?.length > 0 && this.selected?.length === this.options.length;
  }

  /** How the control renders its options: the classic ng-select ('dropdown',
   *  default) or an always-visible set of filter buttons/tiles. Comes from the
   *  widget config (`data.displayMode`) so existing dashboards are untouched. */
  get displayMode(): 'dropdown' | 'buttons' | 'tiles' {
    const mode = this.data?.displayMode;
    return mode === 'buttons' || mode === 'tiles' ? mode : 'dropdown';
  }

  get isButtonMode(): boolean {
    return this.displayMode !== 'dropdown';
  }

  get showAllOption(): boolean {
    return this.data?.showAllOption === true;
  }

  /** The label rendered above the control (all display modes). `name` is what
   *  the builder writes (Display label / title / field); `key` covers widgets
   *  saved before `name` existed so old dashboards aren't label-less. */
  get labelText(): string {
    return this.formatLabel(this.data?.name ?? this.data?.key ?? '');
  }

  /** Format-popup `showLabel` hides the label only on an EXPLICIT false —
   *  absent (old widgets / no format set) keeps it visible. */
  get showLabel(): boolean {
    return this.data?.showLabel !== false && !!this.labelText;
  }

  /** Per-widget appearance overrides (builder Format popup → widgetData keys:
   *  pillBg/pillText/pillBorder/pillActiveBg/pillActiveText/pillRadius/
   *  pillFontSize/labelColor/labelFontSize). Applied as scoped CSS custom properties on the root, so
   *  they override the SAME --dvm-filter-* vars the styles already read — for
   *  this widget only; absent keys keep the theme's values. MEMOIZED on the
   *  data ref (a fresh object each CD cycle would re-write inline styles). */
  private styleVarsCache: Record<string, string> = {};
  private styleVarsKey: unknown = undefined;
  /** The Format popup's STOCK colour defaults (input-control.json). Opening the
   *  popup stamps them onto the widget even when the user changed nothing, and
   *  a stamped default then pins that one dropdown to grey/white while its
   *  untouched siblings follow the theme (Engine / Segment kept a #cccccc
   *  border in a black-bordered rail). An untouched default is NOT a
   *  customization — skip it so the theme's value applies. */
  private static readonly STOCK_STYLE_DEFAULTS: Record<string, string> = {
    '--dvm-filter-bg': '#ffffff',
    '--dvm-form-input-text': '#222222',
    '--dvm-filter-border-color': '#cccccc',
    '--dvm-form-label-color': '#1f2937',
  };
  get styleVars(): Record<string, string> {
    if (this.data !== this.styleVarsKey) {
      const vars: Record<string, string> = {};
      const set = (cssVar: string, value: unknown, px = false) => {
        if (value === undefined || value === null || value === '') return;
        if (String(value).trim().toLowerCase() === GlobalDropdownComponent.STOCK_STYLE_DEFAULTS[cssVar]) return;
        vars[cssVar] = px ? `${Number(value)}px` : String(value);
      };
      set('--dvm-filter-bg', this.data?.pillBg);
      // Hover shade DERIVED from the custom background — the theme's fixed
      // light-grey hover washes out dark custom pills (light text on light
      // hover bg = unreadable). No custom bg → no var → theme fallback.
      const hover = this.hoverShadeOf(this.data?.pillBg);
      if (hover) vars['--dvm-filter-hover-bg'] = hover;
      set('--dvm-form-input-text', this.data?.pillText);
      set('--dvm-filter-border-color', this.data?.pillBorder);
      set('--dvm-filter-active-bg', this.data?.pillActiveBg);
      set('--dvm-filter-active-text', this.data?.pillActiveText);
      set('--dvm-filter-radius', this.data?.pillRadius, true);
      set('--dvm-filter-input-font-size', this.data?.pillFontSize, true);
      set('--dvm-filter-font-family', this.data?.pillFontFamily);
      set('--dvm-form-label-color', this.data?.labelColor);
      set('--dvm-filter-label-size', this.data?.labelFontSize, true);
      this.styleVarsCache = vars;
      this.styleVarsKey = this.data;
    }
    return this.styleVarsCache;
  }

  /** A hover variant of a hex color: light colors darken ~8%, dark colors
   *  lighten ~15% — so hover stays visible AND the pill text stays readable
   *  on any custom color. Returns null for non-hex/absent values. */
  private hoverShadeOf(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
    if (!match) return null;
    let hex = match[1];
    if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('');
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16));
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const shift = luminance > 0.5
      ? (c: number) => Math.round(c * 0.92)
      : (c: number) => Math.round(c + (255 - c) * 0.15);
    const toHex = (c: number) => c.toString(16).padStart(2, '0');
    return `#${toHex(shift(r))}${toHex(shift(g))}${toHex(shift(b))}`;
  }

  // ── Submenu groups (buttons/tiles mode only) ─────────────────────────────
  // `data.optionGroups: [{label, displayLabel?, children[]}]` nests options
  // under a parent pill (e.g. MARI + MXDR inside Mining). `displayLabel`
  // overrides the pill/caption/"All" text only — anchoring, validation and
  // openGroupLabel all key off `label`. Groups are VALIDATED against the
  // live options at render time: children are intersected with the backend
  // options and a group left with no live children is dropped. The submenu
  // shows EXACTLY the configured children — a label that is itself an option
  // is replaced by the group pill (add it to children if it should stay
  // selectable). Ungrouped options keep their original order; a group pill
  // renders at its label's position (or at its first child's position when
  // the label isn't an option). The classic dropdown mode ignores groups
  // entirely and stays flat.

  /** Label of the group whose submenu row is open, or null when all closed. */
  openGroupLabel: string | null = null;

  /** MEMOIZED — rebuilt only when the options or the configured groups change
   *  reference; a fresh array each CD cycle would re-create every pill. */
  private topLevelCache: TopLevelEntry[] = [];
  private topLevelOptionsKey: unknown = undefined;
  private topLevelGroupsKey: unknown = undefined;

  /**
   * Re-order an option list per the widget's configured order
   * (`data.optionOrderMode` + `data.customOptionOrder`, written by the builder's
   * Advanced accordion). Mirrors `orderDropdownOptions` in the layout builder so
   * the builder canvas and the published dashboard can never disagree.
   *
   * Reorder ONLY — no de-duplication. Dedup is the builder's job when it bakes
   * `optionsList`; dropping entries here would silently change what a host
   * passed in.
   *
   * Returns the SAME array reference when the order is already correct, so the
   * `topLevelEntries` identity cache keeps working.
   */
  private applyConfiguredOrder(list: any[]): any[] {
    if (!Array.isArray(list) || list.length < 2) return list;
    const mode = String(this.data?.optionOrderMode ?? 'response');
    let next: any[];

    if (mode === 'asc' || mode === 'desc') {
      const sorted = [...list].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }));
      next = mode === 'desc' ? sorted.reverse() : sorted;
    } else if (mode === 'custom') {
      const requested = String(this.data?.customOptionOrder ?? '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      if (!requested.length) return list;
      // Case-insensitive exact match, first occurrence wins.
      const byLower = new Map<string, any>();
      list.forEach((value) => {
        const key = String(value).trim().toLowerCase();
        if (!byLower.has(key)) byLower.set(key, value);
      });
      const picked = new Set<string>();
      const ordered: any[] = [];
      requested.forEach((value) => {
        const key = value.toLowerCase();
        if (picked.has(key)) return;
        const match = byLower.get(key);
        if (match === undefined) return;   // not a real option — ignored
        picked.add(key);
        ordered.push(match);
      });
      // Anything the author didn't list keeps its original order, at the tail.
      next = [...ordered, ...list.filter((value) => !picked.has(String(value).trim().toLowerCase()))];
    } else {
      return list;   // 'response' / unknown → leave the backend order alone
    }

    const unchanged = next.length === list.length && next.every((value, i) => value === list[i]);
    return unchanged ? list : next;
  }

  private uniqueOptions(list: any[]): any[] {
    if (!Array.isArray(list) || list.length < 2) return list;
    const seen = new Set<string>();
    return list.filter((option) => {
      const value = option && typeof option === 'object' && !Array.isArray(option)
        ? ((option as Record<string, unknown>)['value']
          ?? (option as Record<string, unknown>)['label']
          ?? (option as Record<string, unknown>)['name']
          ?? JSON.stringify(option))
        : option;
      const key = String(value ?? '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  get topLevelEntries(): TopLevelEntry[] {
    const options = this.options || [];
    const rawGroups = Array.isArray(this.data?.optionGroups) ? this.data.optionGroups : null;
    if (options !== this.topLevelOptionsKey || rawGroups !== this.topLevelGroupsKey) {
      this.topLevelCache = this.buildTopLevelEntries(options, rawGroups);
      this.topLevelOptionsKey = options;
      this.topLevelGroupsKey = rawGroups;
    }
    return this.topLevelCache;
  }

  private buildTopLevelEntries(options: any[], rawGroups: any[] | null): TopLevelEntry[] {
    if (!rawGroups?.length) {
      return options.map((value) => ({ kind: 'option', value }));
    }
    // Case-insensitive lookup of each option's position (first win on dups).
    const indexOf = new Map<string, number>();
    options.forEach((opt, i) => {
      const key = String(opt).trim().toLowerCase();
      if (!indexOf.has(key)) indexOf.set(key, i);
    });

    const consumed = new Set<number>();
    const groups: Array<{ label: string; displayLabel: string; children: any[]; showAll: boolean; anchor: number }> = [];
    for (const raw of rawGroups) {
      const label = String(raw?.label ?? '').trim();
      if (!label) continue;
      const displayLabel = String(raw?.displayLabel ?? '').trim();
      const labelIdx = indexOf.get(label.toLowerCase());
      const childIdxs: number[] = [];
      for (const child of Array.isArray(raw?.children) ? raw.children : []) {
        const idx = indexOf.get(String(child).trim().toLowerCase());
        if (idx === undefined || consumed.has(idx) || childIdxs.includes(idx)) continue;
        childIdxs.push(idx);
      }
      if (!childIdxs.length) continue; // nothing live left → drop the group
      childIdxs.forEach((idx) => consumed.add(idx));
      // Label that IS an option is replaced by the group pill: consume it so
      // it doesn't ALSO render as a flat duplicate next to "Q ▾". It only
      // appears in the submenu if the author lists it under children.
      if (labelIdx !== undefined) consumed.add(labelIdx);
      groups.push({
        label,
        displayLabel,
        children: childIdxs.map((idx) => options[idx]),
        showAll: raw?.showAll !== false,
        // Where the group PILL sits: the earliest position the group occupies,
        // counting its label AND its children.
        //
        // This used to prefer labelIdx outright, which broke the custom option
        // order: an author who positions a group by listing one of its CHILDREN
        // ("Mining, MxDR, Oil & Gas") left the label unlisted, so the label fell
        // to the unordered tail and dragged the whole group pill to the end —
        // even though its child was placed second. Taking the minimum honours
        // whichever the author positioned first and is a no-op for the default
        // backend order, where the label already precedes its children.
        anchor: Math.min(...(labelIdx !== undefined ? [labelIdx, ...childIdxs] : childIdxs)),
      });
    }
    if (!groups.length) {
      return options.map((value) => ({ kind: 'option', value }));
    }

    const groupByAnchor = new Map(groups.map((group) => [group.anchor, group]));
    const entries: TopLevelEntry[] = [];
    options.forEach((value, i) => {
      const group = groupByAnchor.get(i);
      if (group) {
        entries.push({ kind: 'group', label: group.label, displayLabel: group.displayLabel, children: group.children, showAll: group.showAll });
      } else if (!consumed.has(i)) {
        entries.push({ kind: 'option', value });
      }
    });
    // A group whose label matched no option anchors at its first child, which
    // is consumed — so every group lands exactly once. Groups with anchors
    // outside the options range can't happen (anchors come from indexOf).
    return entries;
  }

  /** The group whose submenu is open, resolved against the CURRENT entries —
   *  returns null if options changed and the group dropped, so a stale
   *  openGroupLabel can never render a dead submenu. */
  get openGroup(): TopLevelEntry | null {
    if (!this.openGroupLabel) return null;
    return (
      this.topLevelEntries.find(
        (entry) => entry.kind === 'group' && entry.label === this.openGroupLabel,
      ) ?? null
    );
  }

  /** Group pill click only toggles its submenu — selection happens inside.
   *  Kept alongside hover-open so touch devices (no hover) still work. */
  onGroupPillClick(entry: TopLevelEntry): void {
    if (this.data?.disabled) return;
    this.cancelSubmenuClose();
    this.openGroupLabel = this.openGroupLabel === entry.label ? null : (entry.label ?? null);
  }

  // Hover behavior: entering a group pill opens its tray; leaving pill/tray
  // schedules a short-delay close (the delay lets the pointer cross the gap
  // between pill row and tray without the tray vanishing); re-entering either
  // cancels the close. Any selection closes the tray immediately.
  private submenuCloseTimer: ReturnType<typeof setTimeout> | null = null;

  onGroupPillEnter(entry: TopLevelEntry): void {
    if (this.data?.disabled) return;
    this.cancelSubmenuClose();
    this.openGroupLabel = entry.label ?? null;
  }

  scheduleSubmenuClose(): void {
    this.cancelSubmenuClose();
    this.submenuCloseTimer = setTimeout(() => {
      this.openGroupLabel = null;
      this.submenuCloseTimer = null;
    }, 250);
  }

  cancelSubmenuClose(): void {
    if (this.submenuCloseTimer) {
      clearTimeout(this.submenuCloseTimer);
      this.submenuCloseTimer = null;
    }
  }

  /** Pill highlight: any child selected marks the group as active. */
  isGroupActive(entry: TopLevelEntry): boolean {
    return (entry.children || []).some((child) => this.isItemSelected(child));
  }

  isGroupFullySelected(entry: TopLevelEntry): boolean {
    const children = entry.children || [];
    return children.length > 0 && children.every((child) => this.isItemSelected(child));
  }

  /** "All <group>" emits the WHOLE group as a multi-value array — the same
   *  shape a multi-select emits, so targets/recompute/local filtering need no
   *  changes. Clicking it again clears the selection (= remove the filter). */
  onGroupAllClick(entry: TopLevelEntry): void {
    if (this.data?.disabled) return;
    this.selected = this.isGroupFullySelected(entry) ? [] : [...(entry.children || [])];
    this.closeSubmenu();
    this.onChange(this.selected);
  }

  /** Selection made (or top-level pill clicked) → the tray goes away. */
  private closeSubmenu(): void {
    this.cancelSubmenuClose();
    this.openGroupLabel = null;
  }

  /** Button/tile mode: no selection = "All" (same semantics as an empty
   *  dropdown selection — the parent treats [] as "clear the filter"). */
  get isAllActive(): boolean {
    return !this.selected?.length;
  }

  /** Click on one option button/tile. Multi-select toggles it. Single-select
   *  PICKS it and can never unpick it — re-clicking the active option is a
   *  no-op, so a single-select control always has a value (see
   *  `allowsEmptySelection`). Emits through the SAME onChange path as the
   *  ng-select, so parents see no difference. */
  onOptionButtonClick(item: any): void {
    if (this.data?.disabled) return;
    if (this.data?.isMultiSelect) {
      this.selected = this.isItemSelected(item)
        ? this.selected.filter((i) => i !== item)
        : [...(this.selected || []), item];
    } else {
      // Re-click on the active single-select option: nothing to do. Clearing
      // here used to leave the control with NOTHING selected and silently drop
      // the filter, which reads as the widget breaking rather than a choice.
      if (this.isItemSelected(item)) {
        this.closeSubmenu();
        return;
      }
      this.selected = [item];
    }
    this.closeSubmenu();
    this.onChange(this.selected);
  }

  /** Whether an EMPTY selection is a legal state.
   *
   *  Multi-select: yes (no filter). Single-select: only when an explicit "All"
   *  option exists — then [] is the named, visible "All" state the user can see
   *  and get back to. Without it, [] means "nothing selected", which a
   *  single-select control must never end up in. */
  get allowsEmptySelection(): boolean {
    return !!this.data?.isMultiSelect || this.showAllOption;
  }

  /** The synthetic "All" option clears the selection (= remove the filter). */
  onAllOptionClick(): void {
    if (this.data?.disabled || this.isAllActive) return;
    this.selected = [];
    this.onChange(this.selected);
  }

  get placeholderText(): string {
    return this.data?.disabled ? (this.data?.disabledPlaceholder || 'Select a value first') : (this.showAllOption ? 'All' : 'Select value');
  }

  isItemSelected(item: any): boolean {
    return this.selected?.includes(item);
  }

  toggleSelectAll(event: any): void {
    if (this.data?.disabled) {
      return;
    }
    const isChecked = event?.target?.checked;

    // Prevent triggering onChange again
    this.suppressSelectionChange = true;
    // this.selected = isChecked ? [...this.filteredOptions] : [];
    this.selected = isChecked ? [...this.options] : [];

    // Emit manually once
    this.onChangeFn(this.selected);
    this.onTouchedFn();

    const valueToEmit = this.data?.isMultiSelect ? this.selected : this.selected;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data.key;
      // Pass filterListener if available
      const filterListener = this.data?.filterListener || null;
      this.globalFilterService.updateFilterSelection(
        this.data.key,
        valueToEmit,
        this.data?.isQueryParam,
        undefined,
        true,
        filterListener,
      );
    }

    this.filterChanged.emit(valueToEmit);

    setTimeout(() => {
      this.suppressSelectionChange = false;
    }, 0);
  }

  onChange(event: any): void {
    if (this.data?.disabled) return;
    if (this.suppressSelectionChange) return;
    this.onChangeFn(this.selected);
    this.onTouchedFn();

    const valueToEmit = this.data?.isMultiSelect ? this.selected : this.selected;

    if (this.data?.key) {
      this.globalFilterService.activeFilter = this.data?.key;
      // Pass filterListener if available in data.filterListener or parent config
      const filterListener = this.data?.filterListener || null;
      this.globalFilterService.updateFilterSelection(
        this.data?.key,
        valueToEmit,
        this.data?.isQueryParam,
        undefined,
        true,
        filterListener,
      );
    }

    this.filterChanged.emit(valueToEmit);
  }

  onItemClick(item: any): void {
    if (this.data?.disabled) {
      return;
    }
    if (!this.data?.isMultiSelect) {
      // Re-click clears the value ONLY when an explicit "All" option exists to
      // land on; otherwise a single-select would be left with nothing selected.
      if (this.selected === item && this.allowsEmptySelection) {
        // Unselect if same item clicked again
        this.selected = [];
        this.onChangeFn(this.selected);
        this.onTouchedFn();

        const valueToEmit = this.selected;

        if (this.data?.key) {
          this.globalFilterService.activeFilter = this.data?.key;
          // Pass filterListener if available
          const filterListener = this.data?.filterListener || null;
          this.globalFilterService.updateFilterSelection(
            this.data.key,
            valueToEmit,
            this.data?.isQueryParam,
            undefined,
            true,
            filterListener,
          );
        }

        this.filterChanged.emit(valueToEmit);
      }
    }
  }

  removeSelected(item: any): void {
    const next = this.selected.filter((i) => i !== item);
    // Never strip the last value off a single-select that has no "All" to fall
    // back to — same rule the option click and the ng-select's clear obey.
    if (!next.length && !this.allowsEmptySelection) return;
    this.selected = next;
    this.onChange(this.selected); // emit the change
  }

  onDropdownClose() {
    this.isDropdownOpen = false;
    this.searchCtrl.setValue('', { emitEvent: true }); // clears input
  }

  onDropdownOpen(): void {
    this.isDropdownOpen = true;
  }

  writeValue(value: any): void {
    // this.selected = value ?? (this.data?.isMultiSelect ? [] : '');
    this.selected = value || [];
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  /**
   * Display-formatting helpers for template.
   * Per UI Standards (Point 3): filter/dropdown names and values in Capital Case.
   * The textCase can be overridden per-widget via `data.textCase` config.
   */
  formatLabel(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    return formatDisplayText(value, mode);
  }

  formatOption(value: unknown): string {
    const mode = (this.data?.textCase || 'capital') as any;
    // Options are normally primitives, but a config/API can hand over
    // {label,value} objects — unwrap those so the label isn't formatted as an
    // object (which renders blank).
    const raw = value && typeof value === 'object' && !Array.isArray(value)
      ? ((value as Record<string, unknown>)['label']
        ?? (value as Record<string, unknown>)['name']
        ?? (value as Record<string, unknown>)['value'])
      : value;
    return formatValueSafely(raw, mode);
  }

  ngOnDestroy(): void {
    this.cancelSubmenuClose();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
